/**
 * Domain Entity Icons
 *
 * Icon mappings for domain-specific entities throughout the application.
 * Uses Lucide React icon library.
 */

import {
    Activity,
    AlertTriangle,
    BookOpen,
    Brain,
    Briefcase,
    Calculator,
    Calendar,
    CheckSquare,
    ClipboardCheck,
    ClipboardList,
    ClipboardPen,
    Clock,
    Coins,
    CreditCard,
    DollarSign,
    FileStack,
    FileText,
    Flag,
    FolderOpen,
    GraduationCap,
    Grid3x3,
    MessageSquare,
    OctagonAlert,
    Paperclip,
    Route,
    Scale,
    ScrollText,
    Settings,
    ShieldCheck,
    StickyNote,
    Target,
    TrendingUp,
    User,
    Users,
} from 'lucide-react';

/**
 * Icon mappings for domain entities
 *
 * Usage:
 * ```typescript
 * import { ICONS } from '@client/constants/icons';
 *
 * <ICONS.risk className="size-4" />
 * ```
 */
export const ICONS = {
    // Risk Management
    risk: AlertTriangle,
    incident: OctagonAlert,
    control: ShieldCheck,
    controlActivity: Activity,
    controlActivityExecution: ClipboardCheck,
    controlActivityNextActivity: Calendar,
    controlTest: ClipboardCheck,
    controlTestNextTest: Calendar,

    // Tasks & Workflow
    task: CheckSquare,
    manage: ClipboardPen,
    library: FolderOpen,
    history: Clock,
    timeline: Route,

    // Claims & Complaints
    claim: FileText,
    claimSnapshot: ClipboardList,
    complaint: MessageSquare,
    productType: Grid3x3,

    // Training & People
    staffTraining: GraduationCap,
    coi: Users, // Conflict of Interest
    complainant: User,
    personal: User,
    contacts: Users,

    // Compliance & Obligations
    obligation: ScrollText,
    obligationSource: BookOpen,
    regulatoryGuidance: Scale,
    breachExample: AlertTriangle,

    // Settings & Configuration
    handlingInfo: Settings,
    outcome: Target,
    flag: Flag,
    note: StickyNote,

    // Financials
    financials: TrendingUp,
    expenses: DollarSign,
    reserve: Coins,
    payment: CreditCard,
    excess: Calculator,

    // Communication & Documents
    communication: MessageSquare,
    activity: Activity,
    policyInfo: FileText,
    attachments: Paperclip,
    documents: FileStack,

    // Business Entities
    supplier: Briefcase,
    materialArrangement: FileStack,

    // Assessments
    governanceCheck: ClipboardCheck,
    assessment: ClipboardCheck,

    // AI
    ai: Brain,
    triage: ClipboardCheck,
} as const;

export type IconName = keyof typeof ICONS;
